import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { ethers } from 'ethers';
import MicrojobPaymentABI from 'abis/MicrojobPayment';
import { buttonStyles } from '../css/commonStyles';
import {
  colors,
  fieldBlockStyles,
  fieldErrorTextSx,
  formFieldStyles,
  requiredSpanSx,
  tabHeaderRowSx,
  tableCardStyles,
  tableContainerStyles,
} from '../css/revenueItems.styles';
import {
  memberCancelBtnSx,
  memberConfirmBtnSx,
  memberDialogBtnRowSx,
  memberDialogContentSx,
  memberDialogPaperSx,
  memberDialogTitleSx,
  memberDialogTitleWrapSx,
  memberFieldLabelSx,
  memberFieldsStackSx,
  memberHeaderActionBtnSx,
} from '../css/daoMemberModal.styles';
import MemberRowActions from './MemberRowActions';
import { formatRevenueSharingErrorMessage, isTxUserRejection } from '../utils/revenueSharing.utils';
import { revenueSharingMessage } from '../utils/revenueSharingMessage';
import { ensureCorrectChain } from '../utils/vaultChain.utils';
import { isValidMicrojobAddress } from '../utils/microjobRoles.utils';

const walletCellSx = {
  fontFamily: 'monospace',
  fontSize: '0.85rem',
  wordBreak: 'break-all',
};

const MicrojobOperatorTeamSection = ({
  microjobPaymentAddress,
  chainId,
  operatorRows = [],
  isMicrojobAdmin,
  onUpdated,
  rolesLoading = false,
}) => {
  const [newAddress, setNewAddress] = useState('');
  const [addressError, setAddressError] = useState('');
  const [busyKey, setBusyKey] = useState('');
  const [addModalOpen, setAddModalOpen] = useState(false);

  const effectiveAddress = isValidMicrojobAddress(microjobPaymentAddress)
    ? microjobPaymentAddress.trim()
    : '';

  const existingOperatorAddresses = useMemo(
    () => new Set(operatorRows.map((row) => row.address.toLowerCase())),
    [operatorRows],
  );

  const showTxError = (err, fallback) => {
    const msg = formatRevenueSharingErrorMessage(err, fallback);
    if (isTxUserRejection(err)) revenueSharingMessage.warning('Transaction was rejected.');
    else revenueSharingMessage.error(msg);
  };

  const grantOperator = async (account) => {
    await ensureCorrectChain(chainId);
    const provider = new ethers.providers.Web3Provider(window.ethereum, 'any');
    const signer = provider.getSigner();
    const contract = new ethers.Contract(effectiveAddress, MicrojobPaymentABI, signer);
    const adminRole = await contract.ADMIN_ROLE();
    const signerAddress = await signer.getAddress();
    const hasAdmin = await contract.hasRole(adminRole, signerAddress);
    if (!hasAdmin) {
      const err = new Error(
        'Connected wallet must have ADMIN role on the MicrojobPayment contract.',
      );
      err.code = 'NOT_MICROJOB_ADMIN';
      throw err;
    }
    const role = await contract.DAO_OPERATOR_ROLE();
    const tx = await contract.grantRole(role, account);
    await tx.wait();
  };

  const revokeOperator = async (account) => {
    await ensureCorrectChain(chainId);
    const provider = new ethers.providers.Web3Provider(window.ethereum, 'any');
    const signer = provider.getSigner();
    const contract = new ethers.Contract(effectiveAddress, MicrojobPaymentABI, signer);
    const adminRole = await contract.ADMIN_ROLE();
    const signerAddress = await signer.getAddress();
    const hasAdmin = await contract.hasRole(adminRole, signerAddress);
    if (!hasAdmin) {
      const err = new Error(
        'Connected wallet must have ADMIN role on the MicrojobPayment contract.',
      );
      err.code = 'NOT_MICROJOB_ADMIN';
      throw err;
    }
    const role = await contract.DAO_OPERATOR_ROLE();
    const tx = await contract.revokeRole(role, account);
    await tx.wait();
  };

  const handleAddOperator = async () => {
    const addr = newAddress.trim();
    if (!addr) {
      setAddressError('Wallet address is required.');
      return;
    }
    if (!/^0x[0-9a-fA-F]{40}$/.test(addr)) {
      setAddressError('Enter a valid wallet address (0x...).');
      return;
    }
    let checksummed;
    try {
      checksummed = ethers.utils.getAddress(addr);
    } catch {
      setAddressError('Enter a valid wallet address (0x...).');
      return;
    }
    if (existingOperatorAddresses.has(checksummed.toLowerCase())) {
      setAddressError('This wallet is already a Microjob operator.');
      return;
    }

    setAddressError('');
    setBusyKey('add');
    try {
      await grantOperator(checksummed);
      revenueSharingMessage.success('Microjob operator granted.');
      setAddModalOpen(false);
      setNewAddress('');
      if (onUpdated) onUpdated();
    } catch (err) {
      if (err?.code === 'NOT_MICROJOB_ADMIN') {
        revenueSharingMessage.error(err.message);
      } else {
        showTxError(err, 'Failed to grant Microjob operator.');
      }
    } finally {
      setBusyKey('');
    }
  };

  const handleRemoveOperator = async (address) => {
    if (!window.confirm('Remove this Microjob operator?')) return;
    setBusyKey(address.toLowerCase());
    try {
      await revokeOperator(address);
      revenueSharingMessage.success('Microjob operator revoked.');
      if (onUpdated) onUpdated();
    } catch (err) {
      if (err?.code === 'NOT_MICROJOB_ADMIN') {
        revenueSharingMessage.error(err.message);
      } else {
        showTxError(err, 'Failed to revoke Microjob operator.');
      }
    } finally {
      setBusyKey('');
    }
  };

  if (!effectiveAddress) return null;

  return (
    <Box>
      <Box sx={tabHeaderRowSx}>
        <Box>
          <Typography
            sx={{
              color: colors.textPrimary,
              fontFamily: 'Questrial, sans-serif',
              fontSize: '1.125rem',
              fontWeight: 400,
            }}
          >
            Microjob Operator team
          </Typography>
          <Typography
            sx={{
              color: colors.textSecondary,
              fontFamily: 'Questrial, sans-serif',
              mt: 0.75,
              maxWidth: 720,
              fontSize: '0.95rem',
              lineHeight: 1.5,
            }}
          >
            Operators create payout cycles, import job logs (CSV), submit them for organizer review, and execute
            payments after the organizer approves.
          </Typography>
        </Box>
        {isMicrojobAdmin && (
          <Button
            variant="contained"
            disabled={!!busyKey}
            onClick={() => {
              setAddressError('');
              setNewAddress('');
              setAddModalOpen(true);
            }}
            sx={{ ...buttonStyles, ...memberHeaderActionBtnSx }}
          >
            + Add operator
          </Button>
        )}
      </Box>

      <Box sx={{ ...tableCardStyles, mt: 0 }}>
        <TableContainer component={Box} sx={tableContainerStyles}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>Wallet</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rolesLoading && (
                <TableRow>
                  <TableCell colSpan={2} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={22} sx={{ color: colors.accent }} />
                  </TableCell>
                </TableRow>
              )}
              {!rolesLoading &&
                operatorRows.map((row) => (
                  <TableRow key={row.address}>
                    <TableCell sx={walletCellSx}>{row.address}</TableCell>
                    <TableCell align="right">
                      <MemberRowActions
                        activeKey={busyKey}
                        actions={[
                          {
                            key: row.address.toLowerCase(),
                            label: 'Remove',
                            disabled: !isMicrojobAdmin,
                            onClick: () => handleRemoveOperator(row.address),
                          },
                        ]}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              {!rolesLoading && operatorRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={2} align="center" sx={{ py: 4, color: colors.textSecondary }}>
                    No Microjob operators yet.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Dialog
        open={addModalOpen}
        onClose={() => !busyKey && setAddModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: memberDialogPaperSx }}
      >
        <DialogTitle sx={memberDialogTitleWrapSx}>
          <Typography component="span" sx={memberDialogTitleSx}>
            Add Microjob operator
          </Typography>
        </DialogTitle>
        <DialogContent sx={memberDialogContentSx}>
          <Box sx={memberFieldsStackSx}>
            <Box sx={fieldBlockStyles}>
              <Typography sx={memberFieldLabelSx}>
                Wallet address{' '}
                <Box component="span" sx={requiredSpanSx}>
                  *
                </Box>
              </Typography>
              <TextField
                fullWidth
                placeholder="0x..."
                value={newAddress}
                onChange={(e) => {
                  setNewAddress(e.target.value);
                  setAddressError('');
                }}
                error={!!addressError}
                sx={formFieldStyles}
              />
              {addressError && <Typography sx={fieldErrorTextSx}>{addressError}</Typography>}
            </Box>
          </Box>
          <Box sx={memberDialogBtnRowSx}>
            <Button disabled={!!busyKey} onClick={() => setAddModalOpen(false)} sx={memberCancelBtnSx}>
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={!!busyKey}
              onClick={handleAddOperator}
              sx={memberConfirmBtnSx}
            >
              {busyKey === 'add' ? (
                <CircularProgress size={20} sx={{ color: '#1a1d23' }} />
              ) : (
                'Add operator'
              )}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default MicrojobOperatorTeamSection;
